<script setup lang="ts">
import {onMounted, PropType, Ref, ref, useTemplateRef} from "vue";
import {ICellEditorParams} from "ag-grid-community";
import AttachmentLinkUtil from "@/utils/UIConstants/AttachmentLinkUtil.ts";
import DownloadPurpose from "@/utils/constants/DownloadPurpose.ts";
import {Dialog, QSelect, Screen} from "quasar";
import ReferenceGridFieldWidgetUtil from "@/utils/editView/widgets/ReferenceGridFieldWidgetUtil.ts";
import SearchingAndSuggestionUtil from "@/utils/editView/widgets/SearchingAndSuggestionUtil.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import ReferenceDialog from "@/components/dialogs/ReferenceDialog.vue";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {useInputRestrictions} from "@/composables/useInputRestrictions.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import EntityReferenceData = namaTypes.EntityReferenceData;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;
import FieldMetaData = namaTypes.FieldMetaData;

const props = defineProps({
  params: {
    type: Object as PropType<ICellEditorParams>,
    required: true
  }
});
const fieldValue = props.params.value as EntityReferenceData | null | undefined;
let handler = props.params['handler']();
const columnMetadata: FieldMetaData = props.params['columnMetadata'];
const referenceWrapperRef = useTemplateRef<HTMLElement>("referenceWrapperRef");
const referenceFieldStyle = ClientCache.getFieldStyle(handler.entityType, columnMetadata?.fieldId ?? '');
useInputRestrictions(referenceWrapperRef, referenceFieldStyle?.fixedInputLanguage, referenceFieldStyle?.fixedLetterCase);
const fieldUtil = new ReferenceGridFieldWidgetUtil(handler, columnMetadata, ref(fieldValue), props.params?.rowIndex);
fieldUtil.params = props.params;
const formattedValue: Ref<any | null> = fieldUtil.formattedValue();
const loading = ref(false)
const options = ref<EntityReferenceData[]>([]);
const qReferenceSelect = useTemplateRef<QSelect>("qReferenceSelect");
const shouldFilter = ref(false);
const popupVisible = ref(false);
const errorsStore = useErrorsStore();

function getValue() {
  let originalFieldValue = fieldUtil.fieldValue.value || {};
  let cellTextValue = fieldUtil.inputFieldValueRef.value;
  if (SearchingAndSuggestionUtil.createOptionText(originalFieldValue) !== cellTextValue || (cellTextValue && !originalFieldValue))
    fieldUtil.saveChanges();
  return originalFieldValue;
}

onMounted(() => {
  if (qReferenceSelect.value) {
    setTimeout(() => {
      qReferenceSelect.value?.focus();
      if (Screen.lt.sm)
        onDownHandler();
      if (props.params?.eventKey?.length === 1) {
        qReferenceSelect.value?.updateInputValue(props.params?.eventKey);
      }
    })
  }
});

async function onDownHandler(e?: any) {
  shouldFilter.value = true;
  if (!popupVisible.value) {
    qReferenceSelect.value!.updateInputValue(fieldUtil.inputFieldValueRef.value || '', false);
    qReferenceSelect.value!.showPopup();
  }
}

async function filter(val: string | null | undefined) {
  loading.value = true;
  let referencedEntityType = fieldUtil.referencedEntityType.value;
  let contextInfo: FieldActionContextInfo = fieldUtil.presenter.createSearchContextInfo(fieldUtil.fieldId(), props.params?.rowIndex);
  options.value = await fieldUtil.searchFor({
    query: val || "",
    ownerEntityType: fieldUtil.presenter.entityType,
    contextInfo,
    referencedEntityType,
    fieldId: fieldUtil.fieldId(),
    row: props.params?.rowIndex
  }) ?? [];
  loading.value = false;
}

function onFilter(val, update) {
  update(async () => {
      if (shouldFilter.value)
        await filter(val)
      else
        options.value = [];
    },
    newRef => {
      if (val !== '' && options.value.length > 0) {
        setTimeout(() => {
          newRef.setOptionIndex(-1) // reset optionIndex in case there is something selected
          newRef.moveOptionSelection(1, true) // focus the first selectable option and do not update the input-value
        });
      }
    })
}

function onPopupShow() {
  popupVisible.value = true;
}

function onPopupHide() {
  popupVisible.value = false;
}

async function onBlur() {
  if (FieldWidgetUtil.focusedField === fieldUtil)
    FieldWidgetUtil.focusedField = null;
  fieldUtil.isFieldFocused.value = false;
  return await fieldUtil.onUserInputFinished().then(() => {
    shouldFilter.value = false;
    props.params?.stopEditing();
  });
}

function hasDescriptor(ref: any) {
  return ref.optionCaption && ref.optionCaption !== SearchingAndSuggestionUtil.createOptionText(ref);
}

async function onSelect(value?: EntityReferenceData) {
   await fieldUtil.onSelect(value);
  props.params?.stopEditing();
  AGGridUtils.navigateToNextCellAndStartEditing(props.params?.api);
}

async function onEnterKeyDown(event: KeyboardEvent) {
  if (AGGridUtils.isBulkAction(event)) {
    namaUpdateValue(getValue());
    AGGridUtils.dispatchBulkEventFor(qReferenceSelect);
    return;
  }

  if (!popupVisible.value) {
    onBlur().then(() => {
      AGGridUtils.navigateToNextCellAndStartEditing(props.params?.api);
    });
  }
}

function namaUpdateValue(value: EntityReferenceData) {
  fieldUtil.fieldValue.value = value;
}

function customFetchValueFromRow(row: any, decodeColId: string) {
  let pseudoSubField = fieldUtil.fieldMetaData['pseudoRefSubField'];
  if (pseudoSubField) {
    decodeColId = StringUtils.substringBeforeLast(decodeColId, ".");
  }
  return NamaCommon.getFieldValue(row, decodeColId)
}

function onBrowseClicked(e: MouseEvent & KeyboardEvent): void {
  let referencedEntityType = fieldUtil.referencedEntityType.value;
  if (!referencedEntityType) {
    errorsStore.pushErrorStr("You must select Entity Type");
    e.preventDefault();
    return;
  }
  if (e.key === "F6" || e.ctrlKey) {
    let fieldValue = fieldUtil.fetchFieldValue();
    const linkToObject = PlaceUtils.linkToObject(fieldValue);
    let url = linkToObject;
    if (ObjectChecker.isEmptyOrNull(linkToObject))
      url = PlaceUtils.linkToPlace(PlaceUtils.linkToNew(fieldUtil.referencedEntityType.value, fieldUtil.presenter.entityType));
    window.open(url, "_blank");
    return;
  }
  Dialog.create({
    component: ReferenceDialog,
    componentProps: {
      fieldUtil: fieldUtil,
      event: e,
      field: (handler.listVisibleColumns()).find(l => l.id === columnMetadata.id)
    }
  });
}

function onKeyDown(event: MouseEvent & KeyboardEvent) {
  if (event.key === 'F2') {
    onBlur();
  } else if (event.key === 'F4' || event.key === 'F6' || event.key === 'F7' || event.key === 'F8') {
    onBrowseClicked(event);
  } else if (event.key === 'Escape') {
    props.params?.api.stopEditing(true);
    props.params.api.setFocusedCell(props.params.rowIndex, props.params.column);
  }
}

defineExpose({getValue, namaUpdateValue, customFetchValueFromRow});
</script>

<template>
  <div ref="referenceWrapperRef" class="tw-mb-0 tw-h-full grid-enum-selector tw-max-w-full tw-w-full">
    <div class="tw-relative tw-leading-tight tw-h-full tw-max-w-full tw-w-full">
      <q-select
        use-input hide-selected fill-input dense hide-dropdown-icon options-dense
        @filter="onFilter"
        @keydown.enter="onEnterKeyDown"
        @keydown.down="onDownHandler"
        @keydown.f4.stop="onKeyDown"
        @keydown.f6.stop="onKeyDown"
        @keydown.f7.stop.prevent="onKeyDown"
        @keydown.f8.stop="onKeyDown"
        @keydown.f2.stop="onKeyDown"
        @keydown.esc.stop="onKeyDown"
        @dblclick="onDownHandler"
        @popup-hide="onPopupHide"
        @popup-show="onPopupShow"
        input-debounce="0"
        :model-value="formattedValue"
        :loading="loading"
        :options="options"
        option-label="code"
        ref="qReferenceSelect"
        @input-value="fieldUtil.onPartialUserInput"
        @update:model-value="onSelect"
        @blur="onBlur"
        @focus="fieldUtil.onFocus"
        class="tw-bg-[#F8F9FA] tw-shadow tw-appearance-none dark:tw-bg-[#3E3E3E] tw-h-full tw-min-h-full ag-custom-component-popup"
        input-class="tw-bg-transparent focus:tw-ring-0 focus:tw-shadow-none focus:tw-border-none dark:tw-text-namaFontColor-input-dark
      dark:tw-placeholder-gray-300"
        popup-content-class="!tw-max-h-[30vh] navbar-search-popup dark:tw-bg-namaBackground-deep-dark dark:tw-text-namaFontColor-input-dark ag-custom-component-popup"
        options-selected-class="tw-text-base tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-input-dark">
        <template v-slot:option="scope">
          <q-item v-bind="scope.itemProps">
            <q-item-section v-bind:dir="scope.opt.optionDirection">
              <q-item-label v-text="scope.opt.optionText"/>
              <q-item-label caption class="dark:tw-text-[rgb(255,255,255,0.5)]" v-if="hasDescriptor(scope.opt)"
                            v-text="scope.opt.optionCaption"/>
            </q-item-section>
            <q-item-section avatar v-if="scope.opt.optionHasColorOrImg">
              <q-avatar round class="tw-size-6 compact:tw-size-5 ultra:tw-size-4"
                        :style="scope.opt.colorCode?'backgroundColor:'+scope.opt.colorCode:''">
                <img class="tw-w-full tw-h-full" v-if="SearchingAndSuggestionUtil.shouldShowImgInSuggest(fieldUtil.referencedEntityType.value)"
                     :src="AttachmentLinkUtil.downloadURLByRecordId(scope.opt, DownloadPurpose.THUMBNAIL, false, props.params?.data?.item?.itemCode)"/>
              </q-avatar>
            </q-item-section>
          </q-item>
        </template>
      </q-select>
    </div>
  </div>
</template>
